import { apiRequest } from '../../../src/services/api';
import { t, uiLanguage } from '../../../src/services/language';
import { getSettings } from '../../../src/services/settings';
import type { AudioImportFile, AudioImportTranscript, LiveRecordingWindow, TranscriptSegmentDTO } from '../../../src/shared/types';
import { updateFile } from '../idb';
import { uploadAudioBlob } from './meetingArchive';
import { fileId, readRecording, removeRecording, storeBlob } from './recordings';

/**
 * `audioImport.ts`'nin web karşılığı (ana ekran > "Ses dosyası yükle").
 * Masaüstü dosyayı ffmpeg ile denetleyip mp3'e çeviriyor; tarayıcıda dosya
 * olduğu gibi depoya gidiyor, sunucu çevirip yazıya döküyor ve mp3 kopyasını
 * toplantının arşivi için bir süre tutuyor (`keep`). Dosya toplantı arşivlenene
 * kadar tarayıcıda (IndexedDB) duruyor.
 */

const ACCEPT = 'audio/*,video/mp4,video/webm,.mp3,.m4a,.wav,.ogg,.oga,.opus,.webm,.flac,.aac,.mp4';
const MIN_BYTES = 1024;
/** Sunucunun yükleme sınırıyla aynı (web/lib/storage/s3.ts > UPLOAD_MAX_BYTES). */
const MAX_BYTES = 500 * 1024 * 1024;
const MAX_SECONDS = 4 * 60 * 60;
const TRANSCRIBE_TIMEOUT_MS = 15 * 60_000;

/**
 * Dosya seçtirir. Tıklamanın içinde, beklemeden çağrılmalı — tarayıcı dosya
 * penceresini yalnızca kullanıcı etkileşimiyle açıyor.
 */
function chooseFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPT;
    input.style.display = 'none';
    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };
    input.addEventListener('change', () => finish(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => finish(null));
    document.body.appendChild(input);
    input.click();
  });
}

/** Tarayıcının çözebildiği dosyalarda süre; çözemezse 0 (sunucu kendisi ölçüyor). */
function probeDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    const done = (seconds: number) => {
      URL.revokeObjectURL(url);
      audio.removeAttribute('src');
      resolve(Number.isFinite(seconds) && seconds > 0 ? seconds : 0);
    };
    const timer = setTimeout(() => done(0), 15_000);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      clearTimeout(timer);
      done(audio.duration);
    };
    audio.onerror = () => {
      clearTimeout(timer);
      done(0);
    };
    audio.src = url;
  });
}

export async function pickAudioFile(): Promise<AudioImportFile | null> {
  const file = await chooseFile();
  if (!file) return null;

  if (!/^(audio|video)\//.test(file.type) && !/\.(mp3|m4a|wav|ogg|oga|opus|webm|flac|aac|mp4)$/i.test(file.name)) {
    throw new Error(t('Bu dosya bir ses dosyası değil.', 'This is not an audio file.'));
  }
  if (file.size < MIN_BYTES) throw new Error(t('Dosya boş ya da çok küçük.', 'The file is empty or too small.'));
  if (file.size > MAX_BYTES) throw new Error(t('Dosya çok büyük (en fazla 500 MB).', 'The file is too large (500 MB max).'));

  const durationSeconds = await probeDuration(file);
  if (durationSeconds > MAX_SECONDS) {
    throw new Error(t('Dosya 4 saatten uzun.', 'The file is longer than 4 hours.'));
  }

  const filePath = await storeBlob(file, file.name, 'import');
  return {
    filePath,
    fileName: file.name,
    durationSeconds: Math.round(durationSeconds),
    // Toplantının tarihi yükleme anı: dosyanın değiştirilme tarihi kaydın yapıldığı anı göstermiyor.
    recordedAtISO: new Date().toISOString(),
  };
}

export async function transcribeAudioFile(filePath: unknown, options?: { liveWindow?: LiveRecordingWindow }): Promise<AudioImportTranscript> {
  if (typeof filePath !== 'string') throw new Error(t('Geçersiz dosya.', 'Invalid file.'));
  const liveWindow = options?.liveWindow;
  const stored = await readRecording(filePath);
  const settings = await getSettings();
  const estimated = Math.min(MAX_SECONDS, Math.round((await probeDuration(stored.blob)) || (stored.blob.size * 8) / 64_000));

  const uploadId = await uploadAudioBlob(stored.blob, stored.name, '/api/v1/transcription/upload', {
    durationSeconds: estimated,
  });

  const result = await apiRequest<{ segments: TranscriptSegmentDTO[]; durationSeconds: number; keptUploadId?: string }>(
    'POST',
    '/api/v1/transcription/file',
    {
      body: {
        uploadId,
        language: settings.transcribeLanguage,
        uiLanguage: uiLanguage(),
        terms: settings.transcriptTerms,
        // mp3 kopyası toplantının sesi olarak arşivlensin (bkz. meetingArchive.ts).
        keep: true,
        // Canlı kaydın sesiyse canlı oturumun saydığı süre kotadan ikinci kez düşülmez.
        ...(liveWindow ? { liveWindow: { from: liveWindow.fromISO, to: liveWindow.toISO } } : {}),
      },
      timeoutMs: TRANSCRIBE_TIMEOUT_MS,
    }
  );
  if (result.keptUploadId) await updateFile(fileId(filePath), { serverUploadId: result.keptUploadId }).catch(() => {});
  return {
    transcript: Array.isArray(result.segments) ? result.segments : [],
    durationSeconds: Number(result.durationSeconds) || 0,
  };
}

export async function discardAudioFile(filePath: unknown): Promise<void> {
  if (typeof filePath === 'string') await removeRecording(filePath);
}
